import { useContext, useState, useEffect, useRef } from "react"; // Added useRef
import { Star, Trophy, Clock, QrCode, Briefcase, X, Settings, LogOut, Medal, PiggyBank, Ticket, ThumbsUp, Shield, Coins, Package, Backpack, DollarSign, Timer, ShoppingBag, CircleHelp } from "lucide-react";
import { useNavigate, useLocation, Link, useParams } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import QRScanner from "../../components/Acorn/QRScanner";
import Bank from "../Bank/Bank";
import Jobs from "../Jobs/Jobs";
import Shop from "../Store/Shop";                // ← add
import ProfileSettings from "../Profiles/ProfileSettings";
import { HallPassModal } from "../HallPasses/HallPassModal";
import { AcornOrderStatuses } from '../../types/enums';
import { BadgeActivity } from "../dashboard/BadgeActivity";
import { XPProgress } from "../dashboard/XPProgress";
import { ActivityTimeline } from "../dashboard/ActivityTimeline";
import { useStudentBadgeData } from "../../hooks/useStudentBadgeData";
import BadgesPage from "../../pages/BadgesPage";
import GuidePanel from "../common/GuidePanel";
import studentGuide from "../../assets/student-readme.md?raw";

// desktop‑notification helpers -------------------------------
const NOTIF_SUPPORTED = typeof window !== 'undefined' && 'Notification' in window;

/** Request permission on first component mount */
function useRequestNotificationPermission() {
  useEffect(() => {
    if (NOTIF_SUPPORTED && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* ignored */ });
    }
  }, []);
}

/** One‑liner wrapper (safe‑guards permission & support) */
function pushDesktopNotification(title: string, opts?: NotificationOptions) {
  if (NOTIF_SUPPORTED && Notification.permission === 'granted') {
    // eslint‑disable-next-line no-new
    new Notification(title, opts);
  }
}
// ----------------------------------------------------------------

export default function StudentDashboard() {
  const { handleLogout, userProfile, isLoading, isInitialized } = useContext(UserContext);
  const profile = userProfile;
  const [isScanning, setIsScanning] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const storeSlug = profile?.store;
  const [scannerMode, setScannerMode] = useState<"redeem" | "reissue">("redeem");
  const [showBank, setShowBank] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const { storeSlug: routeStoreSlug } = useParams<{ storeSlug?: string }>();
  const [showShop, setShowShop] = useState<boolean>(!!routeStoreSlug); // open Shop if URL has the slug
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showHallPass, setHallPassModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Load badge progress for the current user
  const { studentXP, badgeProgress: studentBadgeProgress } = useStudentBadgeData();

  // Request notification permission
  useRequestNotificationPermission();

  // Ref to store previous profile state for comparison
  const prevProfileRef = useRef(profile);

  // Only set the default visible pane when the routeStoreSlug changes
  // (previous code ran on every render and forced Bank to be visible,
  // overriding Jobs/Badges when navigating). Keep prevProfileRef updates
  // inside the notification effect so that we can compare previous
  // profile values there.
  useEffect(() => {
    setShowBank(!routeStoreSlug); // default to bank if no storeSlug in URL
  }, [routeStoreSlug]);

  // Effect to trigger notifications based on profile changes
  useEffect(() => {
    if (!profile || !prevProfileRef.current || isLoading || !isInitialized) {
      return; // Don't run on initial load or if profile is missing
    }

    const prevProfile = prevProfileRef.current;

    // Check for new pending pickups
    const prevPickups = prevProfile.pendingPickups?.length || 0;
    const currentPickups = profile.pendingPickups?.length || 0;
    if (currentPickups > prevPickups) {
      const newPickup = profile.pendingPickups[profile.pendingPickups.length - 1];
      const itemName = newPickup?.acornOrderItem?.[0]?.products?.productName || 'your order';
      pushDesktopNotification('Pickup Ready!', { body: `${itemName} is ready for pickup at ${newPickup.pickupLocation}.`, tag: `pickup-${newPickup.acornOrderId}` });
    }

    // Check for new job assignments (simple length check)
    const prevJobs = prevProfile.jobs?.length || 0;
    const currentJobs = profile.jobs?.length || 0;
    if (currentJobs > prevJobs) {
       const newJob = profile.jobs[profile.jobs.length - 1];
      pushDesktopNotification('New Job Assigned!', { body: `You've been assigned the job: ${newJob.jobName}.`, tag: `job-${newJob.jobId}` });
    }

    // Check for significant point increase
    const prevPoints = prevProfile.points || 0;
    const currentPoints = profile.points || 0;
    const pointDiff = currentPoints - prevPoints;
    // Notify if points increased by 10 or more, avoiding tiny increments
    if (pointDiff >= 10) {
      pushDesktopNotification('Points Earned!', { body: `You earned ${pointDiff} points. Total: ${currentPoints.toLocaleString()}`, tag: 'points-update' });
    }

    // Check for new achievements (if available in profile)
    // Assuming profile.achievements is an array
    // const prevAchievements = prevProfile.achievements?.length || 0;
    // const currentAchievements = profile.achievements?.length || 0;
    // if (currentAchievements > prevAchievements) {
    //   const newAchievement = profile.achievements[profile.achievements.length - 1];
    //   pushDesktopNotification('Achievement Unlocked!', { body: `You earned the "${newAchievement.name}" achievement!`, tag: `achievement-${newAchievement.id}` });
    // }

    // Check for Hall Pass status changes (if managed via profile)
    // Example: If a hall pass request was approved
    // const prevHallPassStatus = prevProfile.activeHallPass?.status;
    // const currentHallPassStatus = profile.activeHallPass?.status;
    // if (currentHallPassStatus === 'approved' && prevHallPassStatus !== 'approved') {
    //    pushDesktopNotification('Hall Pass Approved!', { body: `Your request for ${profile.activeHallPass.type} pass is approved.`, tag: `hallpass-${profile.activeHallPass.id}` });
    // }

    // Update previous profile reference after processing changes so that
    // next run has the true previous state.
    prevProfileRef.current = profile;

  }, [profile, isLoading, isInitialized]); // Depend on profile to re-run when it changes

  useEffect(() => {
    const effectiveCss = profile?.theme
      ? (profile.theme === "custom" ? profile.customCss?.trim() || "Index" : profile.theme)
      : profile?.customCss?.trim() || "Index";
    let linkEl: HTMLLinkElement | null = null;
    let styleEl: HTMLStyleElement | null = null;
    if (effectiveCss) {
      if (!/\s/.test(effectiveCss)) {
        linkEl = document.createElement("link");
        linkEl.rel = "stylesheet";
        let fileName = "./styles/" + effectiveCss;
        if (!fileName.endsWith(".css")) fileName += ".css";
        if (process.env.NODE_ENV === "development") {
          linkEl.href = `/src/${fileName}`;
        } else {
          linkEl.href = import.meta.env.BASE_URL + fileName;
        }
        document.head.appendChild(linkEl);
      } else {
        styleEl = document.createElement("style");
        styleEl.textContent = effectiveCss;
        document.head.appendChild(styleEl);
      }
    }
    return () => {
      if (linkEl?.parentNode) linkEl.parentNode.removeChild(linkEl);
      if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);
    };
  }, [profile?.theme, profile?.customCss]);

  // keep local state in sync with URL changes
  // This fixes issue where user gets redirected to dashboard instead of intended page after idle period
  useEffect(() => {
    const path = location.pathname;
    
    // Reset all states first (similar to showSubmenu)
    setShowShop(false);
    setShowJobs(false);
    setShowBank(false);
    setShowBadges(false);
    setHallPassModal(false);
    
    // Map routes to state setters with small delay to ensure proper initialization
    const routeHandlers: Record<string, () => void> = {
      '/student/jobs': () => setTimeout(() => setShowJobs(true), 100),
      '/student/bank': () => setTimeout(() => setShowBank(true), 100),
      '/student/badges': () => setTimeout(() => setShowBadges(true), 100),
      '/student/hallpass': () => setTimeout(() => setHallPassModal(true), 100),
    };
    
    // Handle shop routes (both /shop/:storeSlug and when routeStoreSlug is present)
    if (!!routeStoreSlug || path.startsWith('/shop/')) {
      setTimeout(() => setShowShop(true), 100);
    }
    // Handle other student routes
    else if (routeHandlers[path]) {
      routeHandlers[path]();
    }
    // Default to bank for root student path
    else if (path === '/student' || path === '/') {
      setTimeout(() => setShowBank(true), 100);
    }
  }, [location.pathname, routeStoreSlug]);

  const showSubmenu = (
    menu?: 'shop' | 'jobs' | 'bank' | 'badges' | 'hallpass'
  ) => {
    setShowShop(false);
    setShowJobs(false);
    setShowBank(false);
    setShowBadges(false);
    setHallPassModal(false);
    switch (menu) {
      case 'shop':
        if (storeSlug) navigate(`/shop/${storeSlug}`); else navigate('/student');
        setShowShop(true);
        break;
      case 'jobs':
        navigate('/student/jobs');
        setShowJobs(true);
        break;
      case 'bank':
        navigate('/student/bank');
        setShowBank(true);
        break;
      case 'badges':
        navigate('/student/badges');
        setShowBadges(true);
        break;
      case 'hallpass':
        navigate('/student/hallpass');
        setHallPassModal(true);
        break;
    }
  };

  const pendingOrders = (profile?.orders || [])
  .filter(o => o.acornOrderStatusId === AcornOrderStatuses.Pending);

  if (isLoading || !isInitialized) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex min-h-screen">
      {/* Main content container */}
      <div
        className={`flex-grow transition-all duration-300 ${showProfileModal || showGuide ? 'mr-[0%]' : ''
          }`}
      >
        <div className="dashboard-bg">
          {/* Header */}
          <div className="dashboard-header-container">
            <div className="dashboard-inner-container">
              <div className="dashboard-header-content relative">
                {/* Profile Actions */}
                <div className="dashboard-profile-actions">
                  <button
                    onClick={() => setShowProfileModal(true)}
                    className="dashboard-action-button"
                    title="Settings"
                  >
                    <Settings className="w-5 h-5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => {
                      showSubmenu();
                      setShowGuide(true);
                    }}
                    className="dashboard-action-button"
                    title="Help"
                  >
                    <CircleHelp className="w-5 h-5 text-gray-600" />
                  </button>
                  <button
                    onClick={handleLogout}
                    className="dashboard-action-button"
                    title="Log out"
                  >
                    <LogOut className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <div className="relative">
                  <img
                    onClick={() => setIsImageModalOpen(true)}
                    src={profile?.picture ?? profile?.avatar ?? undefined}
                    alt="Student"
                    className="dashboard-profile-picture"
                  />
                </div>
                <div className="dashboard-profile-info">
                  <div className="dashboard-name-container">
                    <h1 className="dashboard-name">{profile?.name || "Sara Gomez"}</h1>
                  </div>
                  <div className="dashboard-stats-container">
                    <p className="dashboard-stats-text">Grade {profile?.grade} • Student ID: {profile?.altId} • </p>
                    <div className="flex items-center gap-2">
                      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                        <Link to="/level" className="dashboard-btn">
                          <Shield className="w-3 h-3 text-purple-500" />
                          Lv. {(profile as any)?.level || "3"}
                        </Link>
                      )}
                      {/* Points with hover tooltip */}
                      <p
                        className="dashboard-grade inline-flex items-center gap-1"
                        title={`General: ${(profile?.general || 0).toLocaleString()} Points\nSavings: ${(profile?.savings || 0).toLocaleString()} Points`}
                      >
                        <Coins className="w-3 h-3 text-yellow-500" />
                        Puma Points {profile?.points ? `${profile.points.toLocaleString()}` : "0 Points"}
                      </p>
                      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                        <button className="dashboard-btn">
                          <ThumbsUp className="w-3 h-3" />
                          <span className="text-xs font-medium text-green-600">+7</span>
                        </button>
                      )}
                      {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                        <button className="dashboard-btn">
                          <Trophy className="w-3 h-3" />
                          <span className="text-xs font-medium text-green-600">+5</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                    {/* Redeem */}
                    <button
                      onClick={() => {
                        showSubmenu('bank');
                        setScannerMode("redeem");
                        setIsScanning(true);
                      }}
                      className="dashboard-btn"
                    >
                      <QrCode className="w-4 h-4" /> Redeem
                    </button>                    {/* Shop */}
                    <button
                      onClick={() => {
                        if (storeSlug) navigate(`/shop/${storeSlug}`);
                        showSubmenu('shop');
                      }}
                      className="dashboard-btn"
                    >
                      <ShoppingBag className="w-4 h-4" /> Shop
                    </button>

                    {/* Badges */}
                    {(
                      <button
                        onClick={() => {
                          showSubmenu('badges');
                        }}
                        className="dashboard-btn"
                      >
                        <Medal className="w-4 h-4" /> Badges
                      </button>
                    )}

                    {/* Jobs */}
                    {(
                      <button
                        onClick={() => {
                          showSubmenu('jobs');
                        }}
                        className="dashboard-btn"
                      >
                        <Briefcase className="w-4 h-4" /> Jobs
                      </button>
                    )}

                    {/* Bank */}
                    <button
                      onClick={() => {
                        showSubmenu('bank');
                      }}
                      className="dashboard-btn"
                    >
                      <PiggyBank className="w-4 h-4" /> Bank
                    </button>

                    {/* Hall Pass */}
                    {false && (
                      <button
                        onClick={() => {
                          showSubmenu();
                          setHallPassModal(true);
                        }}
                        className="dashboard-btn"
                      >
                        <Ticket className="w-4 h-4" /> Hall Pass
                      </button>
                    )}
                    <HallPassModal isOpen={showHallPass} onClose={() => setHallPassModal(false)} />

                  </div>
                  {/* Render the QRScanner modal when scanning */}
                  {isScanning && <QRScanner mode={scannerMode} onClose={() => setIsScanning(false)} />}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="dashboard-main-container">
            <div className="dashboard-grid">
              {false && (
                <>
                  {/* Shop Pickups */}
                  <div className="dashboard-card dashboard-card-padding">
                    <h2 className="text-xl font-semibold dashboard-card-header mb-4 flex items-center">
                      <Package className="w-5 h-5 text-orange-500 mr-2" />
                      Pending Pickups
                    </h2>
                    <div className="space-y-4">
                      {pendingOrders.length > 0 ? (
                        pendingOrders.map((pickup, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <Backpack className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                            <div>
                              <p className="dashboard-text font-medium">
                                Pickup Location: {pickup.pickupLocation}
                              </p>
                              <p className="text-sm text-gray-500">
                                Order Date: {new Date(pickup.acornOrderDate).toLocaleString()}
                              </p>
                              {pickup.acornOrderItem &&
                                pickup.acornOrderItem.map((item: any, idxItem: number) => (
                                  <div key={idxItem}>
                                    <p className="dashboard-text font-medium">
                                      {item.products.productName}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      Point Cost: {item.pointCost}
                                    </p>
                                  </div>
                                ))}

                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500">No pending pickups</p>
                      )}
                    </div>
                  </div>

                  {/* Jobs Activity */}
                  <div className="dashboard-card dashboard-card-padding">
                    <h2 className="text-xl font-semibold dashboard-card-header mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 text-indigo-500 mr-2" />
                      Jobs Activity
                    </h2>
                    <div className="space-y-4">
                      {profile?.jobs?.map((job, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <DollarSign className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                          <div>
                            <p className="dashboard-text font-medium">{job.jobName}</p>
                            <p className="text-sm text-gray-500">
                              Last earned on: {new Date(job.jobLastEarnedOn).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )) || <p className="text-gray-500">No jobs assigned</p>}
                    </div>
                  </div>

                  {/* Bank Activity card */}
                  <div className="dashboard-card dashboard-card-padding">
                    <h2 className="text-xl font-semibold dashboard-card-header mb-4 flex items-center">
                      Bank Activity
                    </h2>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                        <span className="text-gray-600">Savings</span>
                        <span className="text-base sm:text-lg font-bold text-gray-800">{profile?.savings?.toLocaleString()} pts</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                        <span className="text-gray-600">Coupons Redeemed</span>
                        <span className="text-base sm:text-lg font-bold text-gray-800">{profile?.acornsRedeemed}</span>
                      </div>
                    </div>
                  </div>

                  {/* Badge Activity */}
                  {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                    <BadgeActivity />
                  )}
                </>
              )}

              {/* Hall Pass Activity */}
              {false && import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="dashboard-card dashboard-card-padding">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <Ticket className="w-5 h-5 text-teal-500 mr-2" />
                    Hall Pass Activity
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Timer className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-gray-800 font-medium">Library Visit</p>
                        <p className="text-sm text-gray-500">10:30 AM - 5 min</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Timer className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                      <div>x
                        <p className="text-gray-800 font-medium">Restroom Break</p>
                        <p className="text-sm text-gray-500">9:15 AM - 3 min</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {/* Stats Card - Now using real XP system */}
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="dashboard-card dashboard-card-padding">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <Star className="w-5 h-5 text-yellow-500 mr-2" />
                    Points & Achievements
                  </h2>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total XP</span>
                      <span className="text-2xl font-bold text-gray-800">
                        {studentXP.toLocaleString()} XP
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Badges Earned</span>
                      <span className="text-2xl font-bold text-gray-800">
                        {studentBadgeProgress.filter(p => p.currentLevel !== 'none').length}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* XP Progress */}
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <XPProgress />
              )}

              {/* Recent Activity - Now using real activity timeline */}
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (
                <div className="dashboard-card dashboard-card-padding">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <Clock className="w-5 h-5 text-blue-500 mr-2" />
                    Recent Activity
                  </h2>
                  <ActivityTimeline />
                </div>
              )}
            </div>
          </div>
          {showBank && <Bank />}
          {showJobs && <Jobs />}
          {showShop && <Shop />}            {/* ← render inline */}
          {showBadges && <BadgesPage />}
          {isImageModalOpen && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-white p-4 rounded-lg relative">
                <button
                  title="Close"
                  onClick={() => setIsImageModalOpen(false)}
                  className="absolute top-2 right-2 p-1 transition-colors hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
                <img
                  title="Profile Picture"
                  src={profile?.picture || "https://media.istockphoto.com/id/1303482615/photo/portrait-of-a-cheerful-teenage-girl-in-casual-clothes-isolated-in-white-background.jpg?s=612x612&w=0&k=20&c=ykHqwJsNMKmlYyPhLacOAtUsxIGPuv9W0ieM7LjC3jI="}
                  className="max-w-full max-h-[90vh] object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sliding StudentProfile pane */}
      <aside
        className={`bg-white border-l border-gray-200 h-screen transition-all duration-300 overflow-hidden ${showProfileModal ? 'w-[33%]' : 'w-0'
          }`}
      >
        {showProfileModal && (
          <ProfileSettings
            profile={profile}
            onClose={() => setShowProfileModal(false)}
          />
        )}
      </aside>

      {/* Sliding Help pane */}
      <aside
        className={`bg-white border-l border-gray-200 h-screen transition-all duration-300 overflow-hidden ${showGuide ? 'w-[33%]' : 'w-0'
          }`}
      >
        {showGuide && (
          <GuidePanel markdown={studentGuide} onClose={() => setShowGuide(false)} />
        )}
      </aside>
    </div>
  );
}